// Unassign an RF sensor — back to a fresh, unassigned sensor, for EVERYONE.
//
// Opened from two places: the sensor tile's right-click menu (RfSensorMenu) and
// the Unassign… button in the sensor's Settings dialog (RfSensorDialog). Its own
// file because those two import each other's helpers already — keeping it in
// either one would make the other import it back in a circle.
//
// ⚠️ THE COUNT IS SHOWN BEFORE THE WRITE (a DryRun first), the way "Remove room"
// does: switch actions, automations, recipients, alert rules and access rows
// are DELETED, not hidden. Codes, their meanings, the type and the event log
// are kept — hardware facts (rfDevices.js UnassignRfDevice).
//
// ⚠️ THE BUTTON IS NOT THE GATE. canManageRfSensor only decides whether to show
// it; the backend checks the same three (Super Admin, location owner, the
// sensor's MasterUserId) in rfDevices.canManageRf.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert,
  LinearProgress,
} from '@mui/material';
import { useApp } from '../../core/state/AppContext';
import { unassignRfDevice } from '../../core/api/api';
import { rfNumber, rfSensorName } from '../../core/devices/rfIdentify';
import { invalidateRfDirectory } from '../../core/hooks/useRfDirectory';
import { FIT_SM } from './fitDialog';

// A 4xx from these endpoints carries { error } — say it, never "failed".
const errText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;

/** Who may change access / unassign — mirrors the backend's canManageRf. */
export function canManageRfSensor(device, { userId, isSuperAdmin, locationOwnerId }) {
  if (!device) return false;
  if (isSuperAdmin) return true;
  const me = Number(userId) || 0;
  if (!me) return false;
  return me === Number(device.MasterUserId) || me === Number(locationOwnerId);
}

/**
 * @param device  the RfDevice row to unassign, or null for closed
 * @param onClose cancel
 * @param onDone  unassigned — the host reloads / closes
 */
export default function RfUnassignDialog({ device, onClose, onDone }) {
  const { userId } = useApp();
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!device) return;
    setCounts(null);
    setError('');
    setBusy(false);
    unassignRfDevice(device.RfDeviceId, userId, true)
      .then((r) => setCounts(r.data?.counts || {}))
      .catch((e) => setError(errText(e, 'Could not check what would be removed.')));
  }, [device, userId]);
  if (!device) return null;

  const go = async () => {
    setBusy(true);
    setError('');
    try {
      await unassignRfDevice(device.RfDeviceId, userId, false);
      invalidateRfDirectory();
      onDone && onDone();
    } catch (e) {
      setError(errText(e, 'Not unassigned.'));
      setBusy(false);
    }
  };
  const line = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  return (
    <Dialog open onClose={busy ? undefined : onClose} {...FIT_SM}>
      <DialogTitle>Unassign “{rfSensorName(device)}”?</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: 1 }} className="fit-text">
          It goes back to a new, unassigned sensor ({rfNumber(device.RfDeviceId)}) for
          everyone: no name, no location or room, notifications off.
        </Typography>
        {!counts && !error && <LinearProgress />}
        {counts && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            This will DELETE:
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <li>{line(counts.switchActions || 0, 'switch action', 'switch actions')}</li>
              <li>{line(counts.automations || 0, 'automation', 'automations')}</li>
              <li>{line(counts.recipients || 0, 'chosen recipient', 'chosen recipients')}</li>
              <li>{line(counts.alertRules || 0, 'alert rule', 'alert rules')}</li>
              <li>{line(counts.accessRemoved || 0, 'access removal', 'access removals')}</li>
            </Box>
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" className="fit-text">
          Kept: its codes and what each means (Open / Close…), its type and its event history.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="error" variant="contained" onClick={go} disabled={busy || !counts}>
          Unassign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
